import { useAction, useAtom } from '@reatom/npm-react'
import React from 'react'

import { useHapticFeedback } from '@blade/ui-kit'
import { userAtom, userFullNameOrUsernameAtom } from '@root/entities/auth'
import { UserAvatar } from '@root/entities/auth/ui'
import { Deeplink, DeeplinkURL } from '@root/entities/deeplinking'
import { profileAtom } from '@root/entities/profile'
import { UserShort } from '@root/entities/user-short'
import { openEditProfileFormModalEvent } from '@root/features/user/profile-info'
import { EditProfileDrawer } from '@root/features/user/profile-info/edit-profile-drawer'
import { useClipboard } from '@root/shared'

import {
  coinsDrawerAtom,
  commentsDrawerDelugeIdAtom,
  leaderboardTabAtom,
  relationDrawerDelugeIdAtom,
  relationTabAtom,
} from '../../atoms'
import { ProfileHeader as ProfileHeader_ } from '../common/profile-header'
import { ProfileRankTags } from '../common/profile-rank-tags'

import type { ProfileHeaderAction } from '../common/profile-header'

type Props = {}

export const MyProfileHeader: React.FC<Props> = (_props: Props) => {
  const [fullName] = useAtom(userFullNameOrUsernameAtom)

  const [profile] = useAtom(profileAtom)
  const [user] = useAtom(userAtom)
  const [_commentsDrawer, setCommentsDrawerDelugeId] = useAtom(
    commentsDrawerDelugeIdAtom,
  )

  const { copyToClipboard } = useClipboard()
  const haptics = useHapticFeedback()
  const openEditProfileModal = useAction(openEditProfileFormModalEvent)
  const [_leaderboardTab, setLeaderboardTab] = useAtom(leaderboardTabAtom)
  const [_relationTab, setRelationTab] = useAtom(relationTabAtom)
  const [_relationDrawer, setRelationDrawerDelugeId] = useAtom(
    relationDrawerDelugeIdAtom,
  )
  const [_showCoinsDrawer, setShowCoinsDrawer] = useAtom(coinsDrawerAtom)

  const handleLeaderboardClick = () => {
    haptics.impactOccurred('light')
    setLeaderboardTab('trading')
  }

  const handleReferralClick = () => {
    haptics.impactOccurred('light')
    setLeaderboardTab('referral')
  }

  const handleCoinsClick = () => {
    haptics.impactOccurred('light')
    setShowCoinsDrawer(true)
  }
  const handleCommentsClick = () => {
    haptics.impactOccurred('light')
    setCommentsDrawerDelugeId(user?.delugeId)
  }

  const actions: Array<ProfileHeaderAction> = []

  if (profile) {
    actions.unshift({
      icon: 'pen',
      onClick: () => openEditProfileModal(),
    })
  }

  if (user) {
    const url = DeeplinkURL.fromDeeplink(
      new Deeplink.UserProfile({ delugeId: user.delugeId }),
    )

    actions.unshift({
      icon: 'share',
      onClick: () => copyToClipboard(url.toString()),
    })
  }

  return (
    <>
      <ProfileHeader_
        actions={actions}
        avatar={
          <UserAvatar
            size={60}
            withHypedLetter
            outlineWidth={1.5}
            withPreview={false}
          />
        }
        title={profile ? UserShort.someName(profile) : (fullName ?? '-')}
        mainSubtitle={
          <ProfileRankTags
            trading={{
              count: profile?.tradeRank || '-', // 0 and undefined
              onClick: handleLeaderboardClick,
            }}
            referral={{
              count: profile?.referralRank || '-', // 0 and undefined
              onClick: handleReferralClick,
            }}
          />
        }
        connections={{
          onFollowersClick: () => {
            setRelationDrawerDelugeId(user?.delugeId)
            setRelationTab('followers')
          },
          onFollowingsClick: () => {
            setRelationDrawerDelugeId(user?.delugeId)
            setRelationTab('followings')
          },
          followers: profile?.followers.length ?? '-',
          following: profile?.following.length ?? '-',
        }}
        counts={{
          onLikesClick: handleCommentsClick,
          onMentionsClick: handleCommentsClick,
          onCoinsClick: handleCoinsClick,
          coins: profile?.deployedTokensCount ?? '-',
          likes: profile?.likesCount ?? '-',
          mentions: profile?.mentionsCount ?? '-',
        }}
      />
      <EditProfileDrawer />
    </>
  )
}
